import type { Node, NodeProps } from '@xyflow/react';
import type React from 'react';
import { useMemo } from 'react';
import BaseNode, { type BaseNodeData } from './base-node.component';
import type { HandlesProps } from './common/handles.component';

type HazardNodeData = BaseNodeData;
export type HazardNodeType = Node<HazardNodeData>;
type HazardNodeProps = NodeProps<HazardNodeType>;

const className = 'text-sm bg-hazard-bg text-hazard-fg border-hazard-border border font-semibold shadow-lg';
const labelClassName = 'line-clamp-4';
const iconClassName = 'text-hazard-fg stroke-hazard-fg';
const handles: HandlesProps = { bottomHandleId: 'bottom', bottomHandleType: 'source' };
const staticData = { className, labelClassName, iconClassName, handles };

/**
 * A component that represents a hazard node in a flow diagram.
 * Extends the base node component with hazard-specific functionality.
 *
 * @component
 * @param {HazardNodeProps} props - The properties for the hazard node component
 * @returns {JSX.Element} A rendered hazard node component
 *
 * @example
 * ```tsx
 * <HazardNode
 *   id="hazard"
 *   data={{ label: "Hazard" }}
 *   position={{ x: 100, y: 100 }}
 * />
 * ```
 */
const HazardNode = (props: HazardNodeProps): React.JSX.Element => {
  const data = useMemo(() => {
    return { ...props.data, ...staticData };
  }, [props.data.label, props.data.editMode]);

  return <BaseNode {...props} data={data} />;
};

export default HazardNode;
